/*
 * ----------------------------------------------------------------------------
 * RESOLVIA CHAT LOADER
 * ----------------------------------------------------------------------------
 *
 * The livechat used to be pulled in by a site-wide custom-liquid section, so
 * every page start raced a 1.6 MB third-party widget against its own LCP image,
 * fonts and theme JS. Nobody needs the chat that early: the launcher is the
 * Zahnheld speed dial (sections/zh-speed-dial.liquid), and its Livechat item
 * declares `action_type: js` with `action_value: ResolviaChat.open`.
 *
 * This file owns WHEN the widget script arrives, nothing else:
 *
 *   - Idle prewarm. After load, once the browser is idle, the script is fetched
 *     in the background so a later click opens without waiting. Skipped on
 *     data-saver and slow connections, and deferred while the tab is hidden.
 *   - Click path. A capture-phase listener starts the fetch the moment someone
 *     reaches for the chat, in case they are faster than the prewarm.
 *
 * Opening is NOT handled here for the speed dial: zh-speed-dial.js `runAction`
 * already retries a dotted window path for five seconds, so it fires
 * ResolviaChat.open() by itself as soon as the API lands. Only the customer
 * portal CTAs get an explicit open, because their inline handler tries once and
 * gives up.
 *
 * The customer hub keeps its own eager embed (templates/page.contact.json).
 * ensureLoaded() sees that script tag and stays out of the way, so no template
 * needs a special case here.
 */
(function () {
  'use strict';

  var SRC = 'https://resolvia.ai/widget/zahnheld/chat-widget.js';

  // How long after load to wait before prewarming. The single knob for the
  // bytes-versus-readiness trade: raise it and more short visits never pay for
  // the widget at all, lower it and the first click is warm more often.
  // The one knob that actually moves the widget. Measured on this store: with the
  // script eager in a footer section the first Resolvia request landed at a median
  // 481 ms (home) and 543 ms (product), i.e. in the middle of the page's own load.
  // With this delay it lands at 4.8 s, after the load event, so it no longer
  // competes for bandwidth with anything the visitor is waiting for.
  //
  // Raise it and more short visits never pay for the widget at all; drop the
  // prewarm entirely and only the click path remains, which is the variant that
  // truly saves the 1.6 MB, at the price of proactive messages.
  var PREWARM_DELAY_MS = 2500;

  // Upper bound for requestIdleCallback, for a device whose main thread never
  // yields. Measured, this barely matters: raising it from 2 s to 10 s moved the
  // median start by less than half a second, because an idle window does turn up
  // shortly after the delay above. Kept generous so a genuinely saturated device
  // waits rather than forcing the fetch.
  var IDLE_TIMEOUT_MS = 10000;

  // Deliberately shorter than zh-speed-dial.js runAction, which waits 150 attempts: the dial
  // drops a wait as soon as its menu closes, and these CTAs have no such way out. A longer
  // window here only widens the gap in which the chat opens by itself over a page the visitor
  // has long moved on from. Only used for the customer portal CTAs.
  var OPEN_RETRY_LIMIT = 50;
  var OPEN_RETRY_STEP_MS = 100;

  var requested = false;

  function alreadyOnPage() {
    if (window.ResolviaChat) {
      return true;
    }
    // The hub embeds the same script directly. Attribute-prefix selector, so a
    // cache-busting query string on the src still counts as present.
    return Boolean(document.querySelector('script[src^="' + SRC + '"]'));
  }

  function ensureLoaded() {
    if (requested) {
      return;
    }
    if (alreadyOnPage()) {
      requested = true;
      return;
    }

    var script = document.createElement('script');
    script.src = SRC;
    script.async = true;
    (document.head || document.documentElement).appendChild(script);
    requested = true;
  }

  /* --- customer portal CTAs ------------------------------------------------ */

  function openWhenReady(attempt) {
    var chat = window.ResolviaChat;
    if (chat && typeof chat.open === 'function') {
      chat.open();
      return;
    }
    if ((attempt || 0) >= OPEN_RETRY_LIMIT) {
      return;
    }
    window.setTimeout(function () {
      openWhenReady((attempt || 0) + 1);
    }, OPEN_RETRY_STEP_MS);
  }

  /* --- prewarm ------------------------------------------------------------- */

  function connectionSaysNo() {
    var connection = window.navigator && window.navigator.connection;
    if (!connection) {
      return false;
    }
    if (connection.saveData) {
      return true;
    }
    return connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g';
  }

  function schedulePrewarm() {
    if (requested || connectionSaysNo()) {
      return;
    }

    // A hidden tab throttles timers anyway, and prewarming a page nobody is
    // looking at is pure waste. Wait for it to come forward instead.
    if (document.visibilityState === 'hidden') {
      document.addEventListener('visibilitychange', function onVisible() {
        if (document.visibilityState === 'hidden') {
          return;
        }
        document.removeEventListener('visibilitychange', onVisible);
        schedulePrewarm();
      });
      return;
    }

    window.setTimeout(function () {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(ensureLoaded, { timeout: IDLE_TIMEOUT_MS });
        return;
      }
      // No idle API (older Safari): the delay above already did the getting out
      // of the way, so hand it to the next task rather than waiting out the
      // idle timeout a second time.
      window.setTimeout(ensureLoaded, 0);
    }, PREWARM_DELAY_MS);
  }

  /* --- wiring -------------------------------------------------------------- */

  // Capture phase, so the fetch starts before the speed dial's own click
  // handler begins its retry chain.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') {
        return;
      }

      var dialItem = target.closest('[data-zhsd-value^="ResolviaChat"]');
      if (dialItem) {
        ensureLoaded();
        return;
      }

      if (target.closest('[data-cp-chat]')) {
        ensureLoaded();
        openWhenReady(0);
      }
    },
    true
  );

  if (document.readyState === 'complete') {
    schedulePrewarm();
  } else {
    window.addEventListener('load', schedulePrewarm, { once: true });
  }
})();
